import { Capacitor } from "@capacitor/core";

export const NATIVE_OAUTH_REDIRECT_URIS = {
  android: "com.voltyshare.app://auth/callback",
  ios: "com.160418.voltyshare://auth/callback",
} as const;

export type NativeOAuthPlatform = keyof typeof NATIVE_OAUTH_REDIRECT_URIS;

const NATIVE_OAUTH_REDIRECT_URI_ALIASES: Record<NativeOAuthPlatform, readonly string[]> = {
  android: [NATIVE_OAUTH_REDIRECT_URIS.android],
  ios: [NATIVE_OAUTH_REDIRECT_URIS.ios],
};

export const getNativeOAuthPlatform = (): NativeOAuthPlatform =>
  Capacitor.getPlatform() === "ios" ? "ios" : "android";

export const getNativeOAuthRedirectUri = (platform: NativeOAuthPlatform = getNativeOAuthPlatform()) =>
  NATIVE_OAUTH_REDIRECT_URIS[platform];

export const getNativeOAuthRedirectUris = (platform: NativeOAuthPlatform = getNativeOAuthPlatform()) =>
  [...NATIVE_OAUTH_REDIRECT_URI_ALIASES[platform]];

export const NATIVE_OAUTH_REDIRECT_URI = NATIVE_OAUTH_REDIRECT_URIS.android;
// Use the /~oauth namespace because old VoltyShare service workers already
// bypass it. That keeps native OAuth callbacks out of stale PWA navigation
// fallbacks on Android browsers.
export const NATIVE_OAUTH_WEB_BRIDGE_URI = "https://www.voltyshare.com/~oauth/native-callback";
export const NATIVE_OAUTH_SUPABASE_BRIDGE_URI = "https://www.voltyshare.com/auth?native_oauth=1";
export const NATIVE_OAUTH_BROKER_URL = "https://www.voltyshare.com/~oauth/initiate";
export const NATIVE_OAUTH_WEB_BRIDGE_PATH = "/~oauth/native-callback";

const OAUTH_PENDING_KEY = "vs_oauth_pending";
const OAUTH_STATE_TTL_MS = 10 * 60 * 1000;
const OAUTH_STATE_PREFIX = "vsnative";
const NATIVE_OAUTH_STATE_PARAM = "native_state";

type PendingOAuth = {
  mode?: "login" | "register";
  provider?: "google" | "apple";
  redirectTo?: string;
  state?: string;
  states?: PendingOAuthState[];
  startedAt?: number;
};

type PendingOAuthState = {
  redirectTo?: string;
  state: string;
  startedAt: number;
};

type NativeOAuthStartOptions = {
  mode?: "login" | "register";
  redirectTo?: string;
};

export const getOAuthProviderQueryParams = (provider: "google" | "apple") =>
  provider === "google" ? { prompt: "select_account" } : {};

export type NativeOAuthSupabaseRequest = {
  queryParams: Record<string, string>;
  redirectTo: string;
  state: string;
};

const isSafeAppPath = (value: unknown): value is string =>
  typeof value === "string" && value.startsWith("/") && !value.startsWith("//");

const isExactPathOrChild = (pathname: string, expectedPath: string) =>
  pathname === expectedPath || pathname.startsWith(`${expectedPath}/`);

export const isNativeOAuthCallbackUrl = (url: string) => {
  try {
    const parsed = new URL(url);
    const protocolPlatforms = Object.entries(NATIVE_OAUTH_REDIRECT_URI_ALIASES).flatMap(
      ([platform, redirectUris]) =>
        redirectUris.map((redirectUri) => ({
          platform: platform as NativeOAuthPlatform,
          protocol: new URL(redirectUri).protocol,
        })),
    );
    const callbackProtocol = protocolPlatforms.find((entry) => entry.protocol === parsed.protocol);
    const callbackStatePlatform = platformFromState(
      parsed.searchParams.get(NATIVE_OAUTH_STATE_PARAM) || parsed.searchParams.get("state"),
    );
    const isCustomSchemeCallback =
      !!callbackProtocol &&
      ((parsed.hostname === "auth" && isExactPathOrChild(parsed.pathname, "/callback")) ||
        isExactPathOrChild(parsed.pathname, "/auth/callback"));
    const isWebBridgeCallback =
      parsed.protocol === "https:" &&
      (parsed.hostname === "www.voltyshare.com" || parsed.hostname === "voltyshare.com") &&
      isExactPathOrChild(parsed.pathname, NATIVE_OAUTH_WEB_BRIDGE_PATH);

    return (
      (isCustomSchemeCallback &&
        (!callbackStatePlatform || callbackStatePlatform === callbackProtocol.platform)) ||
      isWebBridgeCallback
    );
  } catch {
    return false;
  }
};

const platformFromState = (state: string | null): NativeOAuthPlatform | null => {
  const match = state?.match(/^vsnative_(ios|android)_/);
  return match?.[1] === "ios" || match?.[1] === "android" ? match[1] : null;
};

const getNativeOAuthStateFromPath = (pathname: string) => {
  const match = pathname.match(/^\/~oauth\/native-callback\/(?:ios|android)\/([^/?#]+)/);
  if (!match?.[1]) return null;

  try {
    const state = decodeURIComponent(match[1]);
    return platformFromState(state) ? state : null;
  } catch {
    return null;
  }
};

const getNativeOAuthState = (params: URLSearchParams) => {
  const nativeState = params.get(NATIVE_OAUTH_STATE_PARAM);
  if (platformFromState(nativeState)) return nativeState;

  const legacyState = params.get("state");
  return platformFromState(legacyState) ? legacyState : null;
};

export const getOAuthCallbackParams = (url: string) => {
  const parsed = new URL(url);
  const params = new URLSearchParams(parsed.search);
  const pathState = getNativeOAuthStateFromPath(parsed.pathname);
  const hash = parsed.hash.replace(/^#/, "");

  if (hash) {
    const hashParamsSource = hash.includes("?") ? hash.slice(hash.indexOf("?") + 1) : hash;
    const hashParams = new URLSearchParams(hashParamsSource);
    hashParams.forEach((value, key) => {
      if (!params.has(key)) {
        params.set(key, value);
      }
    });
  }

  if (pathState && !platformFromState(params.get(NATIVE_OAUTH_STATE_PARAM))) {
    params.set(NATIVE_OAUTH_STATE_PARAM, pathState);
  }

  return params;
};

const recentPendingStates = (pending: PendingOAuth, now = Date.now()) => {
  const states = [...(pending.states || [])];
  if (pending.state && pending.startedAt) {
    states.push({
      redirectTo: pending.redirectTo,
      state: pending.state,
      startedAt: pending.startedAt,
    });
  }

  const deduped = new Map<string, PendingOAuthState>();
  states.forEach((entry) => {
    if (!entry.state || !entry.startedAt) return;
    if (now - entry.startedAt > OAUTH_STATE_TTL_MS) return;
    deduped.set(entry.state, {
      redirectTo: isSafeAppPath(entry.redirectTo) ? entry.redirectTo : undefined,
      state: entry.state,
      startedAt: entry.startedAt,
    });
  });

  return [...deduped.values()]
    .sort((a, b) => b.startedAt - a.startedAt)
    .slice(0, 5);
};

export const getPendingOAuthRedirectPath = (fallback = "/search", state?: string | null) => {
  try {
    const pending = JSON.parse(localStorage.getItem(OAUTH_PENDING_KEY) || "{}") as PendingOAuth;
    if (state) {
      const match = recentPendingStates(pending).find((entry) => entry.state === state);
      if (isSafeAppPath(match?.redirectTo)) return match.redirectTo;
      return fallback;
    }
    return isSafeAppPath(pending.redirectTo) ? pending.redirectTo : fallback;
  } catch {
    return fallback;
  }
};

export const clearPendingOAuth = () => {
  localStorage.removeItem(OAUTH_PENDING_KEY);
};

const readPendingOAuth = (): PendingOAuth => {
  try {
    return JSON.parse(localStorage.getItem(OAUTH_PENDING_KEY) || "{}") as PendingOAuth;
  } catch {
    return {};
  }
};

const writePendingOAuthState = (
  provider: "google" | "apple",
  state: string,
  options: NativeOAuthStartOptions = {},
) => {
  const pending = readPendingOAuth();
  const startedAt = Date.now();
  const redirectTo = isSafeAppPath(options.redirectTo)
    ? options.redirectTo
    : isSafeAppPath(pending.redirectTo)
      ? pending.redirectTo
      : undefined;
  const states = recentPendingStates(pending, startedAt).filter((entry) => entry.state !== state);
  states.unshift({ redirectTo, state, startedAt });

  localStorage.setItem(
    OAUTH_PENDING_KEY,
    JSON.stringify({
      ...pending,
      mode: options.mode || pending.mode,
      provider,
      redirectTo,
      state,
      states: states.slice(0, 5),
      startedAt,
    }),
  );
};

export const validatePendingOAuthState = (state: string | null) => {
  if (!state) return false;
  const pending = readPendingOAuth();
  return recentPendingStates(pending).some((entry) => entry.state === state);
};

export const hasPendingOAuthAttempt = () => recentPendingStates(readPendingOAuth()).length > 0;

const buildNativeOAuthCallbackUrlForRedirectUri = (redirectUri: string, params: URLSearchParams) => {
  const callbackParams = new URLSearchParams();
  const nativeState = getNativeOAuthState(params);
  if (nativeState) {
    callbackParams.set("state", nativeState);
  }

  [
    "access_token",
    "refresh_token",
    "expires_in",
    "expires_at",
    "token_type",
    "code",
    "error",
    "error_code",
    "error_description",
  ].forEach((key) => {
    const value = params.get(key);
    if (value) {
      callbackParams.set(key, value);
    }
  });

  const separator = callbackParams.toString() ? `?${callbackParams.toString()}` : "";
  return `${redirectUri}${separator}`;
};

export const buildNativeOAuthCallbackUrls = (params: URLSearchParams) => {
  const nativeState = getNativeOAuthState(params);
  const platform = platformFromState(nativeState) ?? "android";

  return [...new Set(getNativeOAuthRedirectUris(platform))]
    .map((redirectUri) => buildNativeOAuthCallbackUrlForRedirectUri(redirectUri, params));
};

export const buildNativeOAuthCallbackUrl = (params: URLSearchParams) =>
  buildNativeOAuthCallbackUrls(params)[0];

const createOAuthState = (platform: NativeOAuthPlatform) => {
  const random = (() => {
    if (!globalThis.crypto?.getRandomValues) {
      throw new Error("Secure random source is unavailable for native OAuth state");
    }

    const bytes = new Uint8Array(16);
    globalThis.crypto.getRandomValues(bytes);
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  })();

  return `${OAUTH_STATE_PREFIX}_${platform}_${random}`;
};

export const buildNativeOAuthBridgeRedirectUri = (state: string) => {
  const platform = platformFromState(state) ?? getNativeOAuthPlatform();
  const webBridgeUrl = new URL(`${NATIVE_OAUTH_WEB_BRIDGE_URI}/${platform}/${encodeURIComponent(state)}`);
  webBridgeUrl.searchParams.set(NATIVE_OAUTH_STATE_PARAM, state);
  return webBridgeUrl.toString();
};

export const buildNativeOAuthSupabaseBridgeRedirectUri = (state: string) => {
  const webBridgeUrl = new URL(NATIVE_OAUTH_SUPABASE_BRIDGE_URI);
  webBridgeUrl.searchParams.set(NATIVE_OAUTH_STATE_PARAM, state);
  return webBridgeUrl.toString();
};

export const buildNativeOAuthDirectRedirectUri = (state: string) => {
  const platform = platformFromState(state) ?? getNativeOAuthPlatform();
  const callbackUrl = new URL(getNativeOAuthRedirectUri(platform));
  callbackUrl.searchParams.set(NATIVE_OAUTH_STATE_PARAM, state);
  return callbackUrl.toString();
};

export const buildNativeOAuthRedirectUri = (state: string) => {
  // Keep provider auth on the proven HTTPS bridge for both native platforms.
  // The bridge preserves app state in the path, then opens the platform scheme.
  return buildNativeOAuthBridgeRedirectUri(state);
};

export const createNativeOAuthSupabaseRequest = (
  provider: "google" | "apple",
  options: NativeOAuthStartOptions = {},
): NativeOAuthSupabaseRequest => {
  const platform = getNativeOAuthPlatform();
  const state = createOAuthState(platform);
  writePendingOAuthState(provider, state, options);

  return {
    queryParams: getOAuthProviderQueryParams(provider),
    redirectTo:
      platform === "android"
        ? buildNativeOAuthSupabaseBridgeRedirectUri(state)
        : buildNativeOAuthRedirectUri(state),
    state,
  };
};

export const shouldUseDirectSupabaseOAuth = (
  provider: "google" | "apple",
  platform: NativeOAuthPlatform = getNativeOAuthPlatform(),
) => provider === "google" && platform === "android";

export const buildNativeOAuthStartUrl = (
  provider: "google" | "apple",
  options: NativeOAuthStartOptions = {},
) => {
  const state = createOAuthState(getNativeOAuthPlatform());
  writePendingOAuthState(provider, state, options);

  const params = new URLSearchParams({
    provider,
    // Lovable rejects custom-scheme redirect_uri values before provider auth.
    redirect_uri: buildNativeOAuthBridgeRedirectUri(state),
    state,
    ...getOAuthProviderQueryParams(provider),
  });
  return `${NATIVE_OAUTH_BROKER_URL}?${params.toString()}`;
};
